// Data model for the "Att göra"-tool, inspired by goert.app: several separate
// lists, recursively nested subtasks, and four task statuses instead of a plain
// done/not-done. Persisted under users/{uid}.todos.

export type Priority = "low" | "normal" | "high";
export type Status = "todo" | "ongoing" | "done" | "ignored";

export interface Task {
  id: string;
  text: string;
  status: Status;
  due: string;        // "YYYY-MM-DD" or ""
  priority: Priority;
  createdAt: number;
  children: Task[];
  qty: number;        // packing lists: how many to bring
  packed: number;     // packing lists: how many are packed
  group: boolean;     // packing lists: a heading that holds related items
  note: string;       // free text under the item, e.g. "säsong 2, tips från Emma"
}

// "tasks" is the plain to-do list; "pack" is a packing list with counts;
// "notes" is a list of things to remember (series, books, gift ideas) with
// a note per item, nothing to tick off, nothing due and nothing hidden
export type ListKind = "tasks" | "pack" | "notes";

export interface Template {
  id: string;
  name: string;
  icon: string;
  kind: ListKind;
  tasks: Task[];
}

// A shared list lives in its own todoLists/{id} doc instead of the owner's
// users doc, so everyone in sharedWith (by email) can read and edit it
export interface Sharing {
  owner: string;        // uid
  ownerEmail: string;
  sharedWith: string[]; // lower-case emails
}

export interface TodoList {
  id: string;
  name: string;
  icon: string;       // emoji
  tasks: Task[];
  archived: boolean;  // hidden from the sidebar, kept in the archive
  kind: ListKind;
  shared: Sharing | null;
}

export interface TodoData {
  lists: TodoList[];
  activeListId: string;
  templates: Template[];
  order: string[];    // sidebar order of all list ids, shared ones included
}

export const newId = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export const blankTask = (text = ""): Task => ({
  id: newId(),
  text,
  status: "todo",
  due: "",
  priority: "normal",
  createdAt: Date.now(),
  children: [],
  qty: 1,
  packed: 0,
  group: false,
  note: "",
});

export const blankList = (name = "Ny lista", icon = "📝", kind: ListKind = "tasks"): TodoList => ({
  id: newId(),
  name,
  icon,
  tasks: [],
  archived: false,
  kind,
  shared: null,
});

// ── templates ──
// A fresh copy of tasks: new ids, nothing done or packed
const freshTasks = (tasks: Task[]): Task[] =>
  tasks.map((t) => ({ ...t, id: newId(), status: "todo", packed: 0, createdAt: Date.now(), children: freshTasks(t.children) }));

export const listFromTemplate = (t: Template): TodoList => ({ ...blankList(t.name, t.icon, t.kind), tasks: freshTasks(t.tasks) });
export const templateFromList = (l: TodoList): Template =>
  ({ id: newId(), name: l.name, icon: l.icon, kind: l.kind, tasks: freshTasks(l.tasks) });

const item = (text: string, qty = 1): Task => ({ ...blankTask(text), qty });
export const blankGroup = (text = "Ny grupp", children: Task[] = []): Task => ({ ...blankTask(text), group: true, children });
export const BUILTIN_TEMPLATES: Template[] = [
  {
    id: "builtin-pack",
    name: "Packlista resa",
    icon: "🧳",
    kind: "pack",
    tasks: [
      blankGroup("Kläder", [
        item("Strumpor", 5), item("Kalsonger/trosor", 5), item("T-shirt", 4), item("Byxor", 2),
        item("Tröja"), item("Jacka"), item("Pyjamas"), item("Skor", 2),
      ]),
      blankGroup("Träningskläder", [
        item("Träningströja", 2), item("Shorts/tights", 2), item("Sportstrumpor", 2), item("Löparskor"), item("Pulsklocka"),
      ]),
      blankGroup("Hygien", [item("Tandborste"), item("Tandkräm"), item("Schampo"), item("Deodorant"), item("Mediciner")]),
      blankGroup("Teknik", [item("Laddare", 2), item("Hörlurar")]),
      blankGroup("Viktigt", [item("Pass/ID"), item("Plånbok"), item("Nycklar")]),
    ],
  },
  { id: "builtin-series", name: "Serier att se", icon: "📺", kind: "notes", tasks: [] },
  { id: "builtin-books", name: "Böcker att läsa", icon: "📚", kind: "notes", tasks: [] },
  { id: "builtin-gifts", name: "Presenttips", icon: "🎁", kind: "notes", tasks: [] },
];

// Packing: setting the packed count also marks the item done when all are packed
export const withPacked = (t: Task, n: number): Task => {
  const packed = Math.max(0, Math.min(t.qty, n));
  return { ...t, packed, status: packed >= t.qty ? "done" : "todo" };
};

// Packs or unpacks everything in a group
export const packAll = (t: Task, on: boolean): Task => ({ ...t, children: t.children.map((c) => withPacked(c, on ? c.qty : 0)) });

// Things to pack, not counting group headings
export const packItems = (tasks: Task[]): Task[] =>
  tasks.flatMap((t) => (t.group || t.children.length ? packItems(t.children) : [t]));

// ── status / priority metadata ──
export const STATUS_ORDER: Status[] = ["todo", "ongoing", "done", "ignored"];
export const STATUS_META: Record<Status, { label: string; glyph: string }> = {
  todo: { label: "Att göra", glyph: "" },
  ongoing: { label: "Pågår", glyph: "◐" },
  done: { label: "Klar", glyph: "✓" },
  ignored: { label: "Ignorerad", glyph: "–" },
};
export const nextStatus = (s: Status): Status => STATUS_ORDER[(STATUS_ORDER.indexOf(s) + 1) % STATUS_ORDER.length];

export const PRIORITY_LABEL: Record<Priority, string> = { high: "Hög", normal: "Normal", low: "Låg" };

const num = (v: any, d = 0): number => (Number.isFinite(Number(v)) ? Number(v) : d);
const PRIOS: Priority[] = ["low", "normal", "high"];
const asPrio = (v: any): Priority => (PRIOS.includes(v) ? v : "normal");
const asStatus = (v: any, doneFlag?: any): Status =>
  STATUS_ORDER.includes(v) ? v : doneFlag ? "done" : "todo";

const normTask = (t: any): Task => ({
  id: t?.id ?? newId(),
  text: t?.text ?? "",
  status: asStatus(t?.status, t?.done),
  due: typeof t?.due === "string" ? t.due : "",
  priority: asPrio(t?.priority),
  createdAt: num(t?.createdAt, Date.now()),
  children: Array.isArray(t?.children) ? t.children.map(normTask) : [],
  qty: Math.max(1, Math.round(num(t?.qty, 1))),
  packed: Math.max(0, Math.round(num(t?.packed, 0))),
  group: !!t?.group,
  note: typeof t?.note === "string" ? t.note : "",
});

const asKind = (v: any): ListKind => (v === "pack" || v === "notes" ? v : "tasks");

const normList = (l: any): TodoList => ({
  id: l?.id ?? newId(),
  name: l?.name ?? "Lista",
  icon: l?.icon ?? "📝",
  tasks: Array.isArray(l?.tasks) ? l.tasks.map(normTask) : [],
  archived: !!l?.archived,
  kind: asKind(l?.kind),
  shared: null,
});

// A todoLists doc as a list
export const listFromDoc = (id: string, d: any): TodoList => ({
  ...normList({ ...d, id }),
  shared: {
    owner: String(d?.uid ?? ""),
    ownerEmail: String(d?.ownerEmail ?? ""),
    sharedWith: Array.isArray(d?.sharedWith) ? d.sharedWith.map(String) : [],
  },
});

// The fields of a shared list that are kept in its todoLists doc
export const sharedFields = (l: TodoList) => ({
  name: l.name, icon: l.icon, kind: l.kind, tasks: l.tasks, archived: l.archived,
  sharedWith: l.shared?.sharedWith ?? [],
});
export const normEmail = (e: string) => e.trim().toLowerCase();

export type Remote = Map<string, TodoList>;

// Takes in what the todoLists listeners last saw: lists that changed there
// replace the local copy, new ones are added, and shared lists that are gone
// (deleted, or no longer shared with you) are dropped. A listener that has
// not answered (null) never drops anything.
export const mergeRemote = (d: TodoData, uid: string, own: Remote | null, theirs: Remote | null, changed: Set<string>): TodoData => {
  const remote: Remote = new Map([...Array.from(theirs ?? []), ...Array.from(own ?? [])]);
  const lists = d.lists
    .filter((l) => !l.shared || remote.has(l.id) || !(l.shared.owner === uid ? own : theirs))
    .map((l) => (changed.has(l.id) && remote.has(l.id) ? remote.get(l.id)! : l));
  remote.forEach((l, id) => { if (!lists.some((x) => x.id === id)) lists.push(l); });
  const at = (id: string) => { const i = d.order.indexOf(id); return i < 0 ? Number.MAX_SAFE_INTEGER : i; };
  lists.sort((a, b) => at(a.id) - at(b.id));
  if (!lists.length) return d;
  return { ...d, lists, activeListId: lists.some((l) => l.id === d.activeListId) ? d.activeListId : lists[0].id };
};


// Accepts the new {lists,activeListId} shape, or migrates the old flat Todo[].
export const normalizeData = (raw: any): TodoData => {
  if (raw && Array.isArray(raw.lists)) {
    const lists: TodoList[] = raw.lists.map(normList);
    if (lists.length === 0) lists.push(blankList("Att göra", "✅"));
    const activeListId = lists.find((l) => l.id === raw.activeListId)?.id ?? lists[0].id;
    const templates: Template[] = Array.isArray(raw.templates)
      ? raw.templates.map((t: any) => ({
          id: t?.id ?? newId(),
          name: t?.name ?? "Mall",
          icon: t?.icon ?? "📝",
          kind: asKind(t?.kind),
          tasks: Array.isArray(t?.tasks) ? t.tasks.map(normTask) : [],
        }))
      : [];
    const order = Array.isArray(raw.order) ? raw.order.map(String) : [];
    return { lists, activeListId, templates, order };
  }
  // migration from the old flat array (users/{uid}.todos = Todo[])
  const migrated = blankList("Att göra", "✅");
  if (Array.isArray(raw)) migrated.tasks = raw.map(normTask);
  return { lists: [migrated], activeListId: migrated.id, templates: [], order: [] };
};

// ── immutable tree helpers ──
export const mapTask = (tasks: Task[], id: string, fn: (t: Task) => Task): Task[] =>
  tasks.map((t) => (t.id === id ? fn(t) : { ...t, children: mapTask(t.children, id, fn) }));

export const removeTask = (tasks: Task[], id: string): Task[] =>
  tasks.filter((t) => t.id !== id).map((t) => ({ ...t, children: removeTask(t.children, id) }));

export const addChild = (tasks: Task[], parentId: string, child: Task): Task[] =>
  tasks.map((t) => (t.id === parentId ? { ...t, children: [...t.children, child] } : { ...t, children: addChild(t.children, parentId, child) }));

export const findTask = (tasks: Task[], id: string): Task | null => {
  for (const t of tasks) {
    if (t.id === id) return t;
    const hit = findTask(t.children, id);
    if (hit) return hit;
  }
  return null;
};

// Moves a task (with its subtasks) to the end of another list's top level
export const moveTask = (data: TodoData, taskId: string, toListId: string): TodoData => {
  const from = data.lists.find((l) => findTask(l.tasks, taskId));
  const task = from && findTask(from.tasks, taskId);
  if (!from || !task || from.id === toListId) return data;
  return {
    ...data,
    lists: data.lists.map((l) =>
      l.id === from.id ? { ...l, tasks: removeTask(l.tasks, taskId) }
      : l.id === toListId ? { ...l, tasks: [...l.tasks, task] }
      : l),
  };
};

// Puts a task right before or after another one, at that task's level
// (a group dropped inside another group lands next to that group instead)
export const placeTask = (tasks: Task[], taskId: string, targetId: string, after: boolean): Task[] => {
  const task = findTask(tasks, taskId);
  if (task?.group && !tasks.some((t) => t.id === targetId)) {
    const outer = parentOf(tasks, targetId);
    if (outer) targetId = outer.id;
  }
  if (!task || taskId === targetId || findTask(task.children, targetId)) return tasks;
  const insert = (ts: Task[]): Task[] =>
    ts.flatMap((t) => (t.id === targetId ? (after ? [t, task] : [task, t]) : [{ ...t, children: insert(t.children) }]));
  return insert(removeTask(tasks, taskId));
};

// The top-level task that holds a nested one
export const parentOf = (tasks: Task[], id: string): Task | null =>
  tasks.find((t) => t.id !== id && findTask(t.children, id)) ?? null;

// Puts an item last in a group, or last at the top level when groupId is ""
export const nestTask = (tasks: Task[], taskId: string, groupId: string): Task[] => {
  const task = findTask(tasks, taskId);
  if (!task || task.group || taskId === groupId) return tasks;
  const rest = removeTask(tasks, taskId);
  return groupId ? rest.map((t) => (t.id === groupId ? { ...t, children: [...t.children, task] } : t)) : [...rest, task];
};

// Swaps a task with the nearest sibling above or below that is not skipped
export const shiftTask = (tasks: Task[], id: string, by: -1 | 1, skip: (t: Task) => boolean = () => false): Task[] => {
  const i = tasks.findIndex((t) => t.id === id);
  if (i < 0) return tasks.map((t) => ({ ...t, children: shiftTask(t.children, id, by, skip) }));
  let j = i + by;
  while (j >= 0 && j < tasks.length && skip(tasks[j])) j += by;
  if (j < 0 || j >= tasks.length) return tasks;
  const out = [...tasks];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

// progress across all leaf descendants: done / (total excluding ignored)
export interface Progress { done: number; total: number; }
export const progress = (t: Task): Progress => {
  if (t.children.length === 0) return { done: 0, total: 0 };
  let done = 0, total = 0;
  const walk = (task: Task) => {
    task.children.forEach((c) => {
      if (c.children.length) { walk(c); return; }
      if (c.status === "ignored") return;
      total += 1;
      if (c.status === "done") done += 1;
    });
  };
  walk(t);
  return { done, total };
};

// ── due-date helpers ──
export type DueStatus = "none" | "future" | "today" | "overdue";
export const dueStatus = (due: string): DueStatus => {
  if (!due) return "none";
  const d = new Date(`${due}T00:00:00`);
  if (isNaN(d.getTime())) return "none";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = d.getTime() - today.getTime();
  if (diff < 0) return "overdue";
  if (diff < 24 * 3600 * 1000) return "today";
  return "future";
};

export const formatDue = (due: string): string => {
  const d = new Date(`${due}T00:00:00`);
  if (isNaN(d.getTime())) return due;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / (24 * 3600 * 1000));
  if (days === 0) return "Idag";
  if (days === 1) return "Imorgon";
  if (days === -1) return "Igår";
  if (days > 1 && days <= 7) return `Om ${days} dgr`;
  if (days < 0) return `${Math.abs(days)} dgr sen`;
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
};

// list-level counts for the sidebar badge (open = not done, not ignored)
export const openCount = (tasks: Task[]): number =>
  tasks.reduce((n, t) => n + (t.group || t.status === "done" || t.status === "ignored" ? 0 : 1) + openCount(t.children), 0);

// ── overview ──
const dayMs = 24 * 3600 * 1000;
const daysTo = (due: string, today: Date): number => {
  const d = new Date(`${due}T00:00:00`);
  return isNaN(d.getTime()) ? Infinity : Math.round((d.getTime() - today.getTime()) / dayMs);
};
const startOfToday = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };

// Open tasks (subtasks too) that are late, due today, or due within a week
export const dueSummary = (tasks: Task[], today = startOfToday()) => {
  const s = { overdue: 0, today: 0, week: 0 };
  const walk = (ts: Task[]) => ts.forEach((t) => {
    if (!t.group && t.due && t.status !== "done" && t.status !== "ignored") {
      const n = daysTo(t.due, today);
      if (n < 0) s.overdue++; else if (n === 0) s.today++; else if (n <= 7) s.week++;
    }
    walk(t.children);
  });
  walk(tasks);
  return s;
};

// Top-level order for viewing: your own, by deadline, or by priority. The
// stored order is never changed by this.
export type SortBy = "own" | "due" | "prio";
const PRIO_RANK: Record<Priority, number> = { high: 0, normal: 1, low: 2 };
const dueRank = (t: Task) => (t.due ? new Date(`${t.due}T00:00:00`).getTime() || Infinity : Infinity);
export const sortTasks = (tasks: Task[], by: SortBy): Task[] => {
  if (by === "own") return tasks;
  const cmp = (a: Task, b: Task) => (by === "due"
    ? dueRank(a) - dueRank(b) || PRIO_RANK[a.priority] - PRIO_RANK[b.priority]
    : PRIO_RANK[a.priority] - PRIO_RANK[b.priority] || dueRank(a) - dueRank(b));
  // stable: equal rows keep their own order
  return tasks.map((t, i) => [t, i] as const).sort(([a, i], [b, j]) => cmp(a, b) || i - j).map(([t]) => t);
};
